/**
 * 将指定的字符串按千分位进行分隔
 * @param val
 * @returns
 */
export const thousands = (val: string) => {
  if (!val) {
    return '';
  }
  let output = `${val}`;
  if (!output.includes('.')) {
    output += '.';
  }
  return output.replace(/(\d)(?=(\d{3})+\.)/g, (_$0, $1) => `${$1},`).replace(/\.$/, '');
};

/**
 * 格式化输入框里面的值
 * 主要处理逻辑是：
 * 1. 将参数的指定字符串全部清空
 * 2. 按小数位分隔
 * 3. 将正整数部分做千分位分隔
 * @param val
 * @param toFixed
 * @returns
 */
export const formatThousandsValue = (val: string, toFixed: number) => {
  if (!val) {
    return '';
  }

  const splitArray = beautifyInputValue(val, toFixed).split('.');

  if (splitArray?.[0]) {
    splitArray[0] = thousands(parseFloat(splitArray[0]).toString());
  } else {
    splitArray[0] = '0';
  }
  if (splitArray?.[1]) {
    splitArray[1] = splitArray[1].replace(/[^0-9]/g, '').slice(0, toFixed);
  }

  // 如果用户尝试输入多个小数点，我们需要将数据做合理的裁切
  return splitArray.splice(0, 2).join('.');
};

/**
 * 格式化数字，将数据转换为浮点之后再转换成字符串
 * @param val
 * @param toFixed
 * @returns
 */
export const beautifyInputValue = (val: string, toFixed: number) => {
  if (!val) {
    return '';
  }
  const tempValue = val.replace('..', '.').replace(/[^0-9.]/g, '');
  if (!tempValue) {
    return '';
  }
  // 如果用户只输入一个点，并且有指定小数位的话
  if (tempValue === '.' && toFixed) {
    return '0.';
  }
  if (tempValue === '.' && toFixed === 0) {
    return '0';
  }

  // 如果限制小数点为0，则直接返回数组里面的第1位
  if (toFixed === 0) {
    return parseFloat(tempValue)
      .toString()
      .split('.')[0];
  }
  return tempValue;
};

/**
 * 验证输入框的值是否合法
 * 1. 必须是合法的数值
 * 2. 有且只能是1个小数点
 * 3. 有且只能是toFixed位小数
 *
 * 根据规则，检测输入框的值的合法性
 * 根据不同的状态，给予不同的状态
 * 通过这个状态，底下的边框会呈现不同的颜色
 * 检验输入框的值是否被清空：status=1
 * 检验输入框的值是否合法：status=2
 * 检验输入框的值是否大于最大值：status=3
 * 检验输入框的值是否小于最小值：status=4
 * @param val
 * @param max
 * @param min
 * @param toFixed
 * @returns
 */
export const validateInputValue = (val: string, max: number, min: number, toFixed = 2) => {
  // 数字不合法，比如包含了非数字相关的字符串，则就不合法
  if (!/^[0-9.,]*$/.test(val)) {
    return 2;
  }
  // 小数点太多，认为不合法
  const tempValue = val.replace(/[^0-9.]/g, '');
  // 空值，认为所输入的信息是合法的
  // 至于业务层认为是否合法，则由业务层自己决定
  if (!tempValue) {
    return 1;
  }
  const splitArray = tempValue.split('.');
  if (splitArray.length > 2) {
    return 2;
  }
  // 如果小数位超过指定位数，认为不合法
  if (splitArray?.[1]?.length > toFixed) {
    return 2;
  }
  if (max.toString() !== '' && isNaN(max) === false && parseFloat(tempValue) > max) {
    return 3;
  }
  if (min.toString() !== '' && isNaN(min) === false && parseFloat(tempValue) < min) {
    return 4;
  }
  // 其它未约定及考虑的范围，认为合法
  return 0;
};

/**
 * 根据长度，返回一个输入值的单位
 * @param val
 * @param length
 * @returns
 */
export const getUnit = (val: string) => {
  const tempValue = beautifyInputValue(val, 0);
  const map: {
    [key: string]: string;
  } = {
    '3': '百',
    '4': '千',
    '5': '万',
    '6': '十万',
    '7': '百万',
    '8': '千万',
    '9': '亿',
    '10': '十亿',
    '11': '百亿',
    '12': '千亿',
    '13': '万亿',
    '14': '十万亿',
  };
  return map[tempValue.length.toString() as string] || '';
};
